/**
 * Email store — compose slice (same-origin /email/v1 via js-routes).
 * Reply/forward draft fields: @tshmanager/email-shared buildComposeDraftFields (web SoT).
 */
import {
  buildComposeDraftFields,
  buildEmailRulesPath,
  buildEmailRulePath,
  buildEmailRuleTogglePath,
  buildEmailRulesReorderPath,
} from '@tshmanager/email-shared'
import { emailApiBase } from './api.js'
import { emailsInComposeTimelineOrder } from './compose-timeline.js'

export { emailsInComposeTimelineOrder } from './compose-timeline.js'

function threadEmails(ctx) {
  const emails = (ctx.thread.value?.items || [])
    .filter(i => i.type === 'email' && i.email)
    .map(i => i.email)
  return emailsInComposeTimelineOrder(emails)
}

function findOriginalEmail(ctx, emailId) {
  const emails = threadEmails(ctx)
  return emails.find(e => Number(e.id) === Number(emailId)) || null
}

function mailboxSendAddress(ctx, mailboxId) {
  const mailbox = (ctx.mailboxes.value || []).find(m => Number(m.id) === Number(mailboxId))
  // Replies must self-exclude the outbound From (`from_email`); `email` is the
  // inbound address and only a fallback when the mailbox JSON lacks from_email.
  return mailbox?.from_email || mailbox?.email || ''
}

function localeTag(ctx) {
  return ctx.i18n?.locale === 'en' ? 'en-GB' : 'hu-HU'
}

function label(ctx, key, fallback) {
  const value = ctx.i18n?.t?.(key)
  if (!value || /^\[missing /.test(value)) return fallback
  return value
}

function composeLabels(ctx) {
  return {
    wrote: label(ctx, 'emails.wrote', 'wrote:'),
    forward: {
      forwardedMessage: label(ctx, 'emails.forwarded_message', 'Forwarded message'),
      from: label(ctx, 'emails.from_label', 'From'),
      date: label(ctx, 'emails.date_label', 'Date'),
      subject: label(ctx, 'emails.subject_label', 'Subject'),
      to: label(ctx, 'emails.to', 'To'),
      cc: label(ctx, 'emails.cc', 'Cc'),
    },
  }
}

function buildPrefetchPayload(ctx, type, emailId) {
  const emails = threadEmails(ctx)
  const original = findOriginalEmail(ctx, emailId)
  if (!original) return null

  const mailboxId = original.mailbox_id || ctx.thread.value?.mailbox_id
  const mailboxEmail = mailboxSendAddress(ctx, mailboxId)

  return buildComposeDraftFields({
    mode: type,
    emails,
    clickedEmailId: emailId,
    mailboxEmail,
    mailboxId: mailboxId != null ? Number(mailboxId) : undefined,
    subject: original.subject || ctx.thread.value?.subject || '',
    labels: composeLabels(ctx),
    localeTag: localeTag(ctx),
  })
}

export function attachCompose(ctx) {
  ctx.openCompose = async function openCompose(type = 'new', originalEmailId = null) {
    const id = originalEmailId ? Number(originalEmailId) : null
    const isReplyFlow = ['reply', 'reply_all', 'forward'].includes(type) && id

    if (isReplyFlow) {
      const data = buildPrefetchPayload(ctx, type, id)
      if (data) {
        ctx.batch(() => {
          ctx.composeOpen.value = true
          ctx.composeType.value = type
          ctx.draftId.value = null
          ctx.composeDirty.value = false
          ctx.composeDraftData.value = data
          ctx.composeOriginalEmailId.value = id
        })
        if (ctx.isMobileLayout.value) ctx.showReadingPane()
        ctx.syncInboxUrl()
        return
      }
    }

    ctx.batch(() => {
      ctx.composeOpen.value = true
      ctx.composeType.value = type
      ctx.draftId.value = null
      ctx.composeDirty.value = false
      ctx.composeDraftData.value = null
      ctx.composeOriginalEmailId.value = id
    })
    if (ctx.isMobileLayout.value) ctx.showReadingPane()
    ctx.syncInboxUrl()
  }

  ctx.openComposeFromDraft = function openComposeFromDraft(draft) {
    ctx.batch(() => {
      ctx.composeOpen.value = true
      ctx.composeType.value = draft.draft_type || 'new'
      ctx.draftId.value = draft.id
      ctx.composeDirty.value = false
      ctx.composeDraftData.value = draft
      ctx.composeOriginalEmailId.value = draft.reply_to_email_id ? Number(draft.reply_to_email_id) : null
    })
    if (ctx.isMobileLayout.value) ctx.showReadingPane()
    ctx.syncInboxUrl()
  }

  ctx.closeCompose = function closeCompose() {
    ctx.batch(() => {
      ctx.composeOpen.value = false
      ctx.draftId.value = null
      ctx.composeDirty.value = false
      ctx.composeDraftData.value = null
      ctx.composeOriginalEmailId.value = null
      ctx.pendingSendEmailId.value = null
      ctx.composeSendOutcome.value = null
      if (ctx.isMobileLayout.value && ctx.currentThreadId.value == null) {
        ctx.activePanel.value = 'list'
      }
    })
    ctx.syncInboxUrl()
  }

  ctx.setPendingSendEmailId = function setPendingSendEmailId(emailId) {
    ctx.pendingSendEmailId.value = emailId == null ? null : Number(emailId)
    ctx.composeSendOutcome.value = null
  }

  ctx.clearComposeSendOutcome = function clearComposeSendOutcome() {
    ctx.composeSendOutcome.value = null
  }

  ctx._handleComposeSendCable = function _handleComposeSendCable(type, emailId) {
    const pending = ctx.pendingSendEmailId.value
    if (pending == null || Number(emailId) !== Number(pending)) return
    ctx.composeSendOutcome.value = { type, email_id: Number(emailId) }
  }

  ctx.openRules = function openRules(mailboxId = null) {
    const mbs = ctx.mailboxes.value
    const preferred = mailboxId ?? ctx.activeMailbox.value ?? mbs[0]?.id ?? null
    ctx.batch(() => {
      ctx.composeOpen.value = false
      ctx.draftId.value = null
      ctx.composeDirty.value = false
      ctx.composeDraftData.value = null
      ctx.composeOriginalEmailId.value = null
      ctx.pendingSendEmailId.value = null
      ctx.composeSendOutcome.value = null
      ctx.rulesOpen.value = true
      ctx.rulesMailboxId.value = preferred ? Number(preferred) : null
      ctx.rulesEditingId.value = null
    })
    if (preferred) ctx.fetchRules(preferred)
    ctx.syncInboxUrl()
  }

  ctx.closeRules = function closeRules() {
    ctx.batch(() => {
      ctx.rulesOpen.value = false
      ctx.rulesEditingId.value = null
      ctx.rules.value = []
    })
    ctx.syncInboxUrl()
  }

  ctx.setRulesMailbox = function setRulesMailbox(mailboxId) {
    ctx.rulesMailboxId.value = mailboxId ? Number(mailboxId) : null
    ctx.rulesEditingId.value = null
    if (mailboxId) ctx.fetchRules(mailboxId)
    else ctx.rules.value = []
  }

  ctx.fetchRules = async function fetchRules(mailboxId = ctx.rulesMailboxId.value) {
    if (!mailboxId) {
      ctx.rules.value = []
      return
    }
    ctx.rulesLoading.value = true
    try {
      const data = await ctx.apiGet(buildEmailRulesPath(emailApiBase(), { mailboxId: Number(mailboxId) }))
      ctx.rules.value = Array.isArray(data) ? data : []
    } catch (err) {
      console.error('[emailStore] fetchRules error:', err)
      ctx.rules.value = []
    } finally {
      ctx.rulesLoading.value = false
    }
  }

  ctx.startNewRule = function startNewRule() {
    ctx.rulesEditingId.value = 'new'
  }

  ctx.startEditRule = function startEditRule(ruleId) {
    ctx.rulesEditingId.value = Number(ruleId)
  }

  ctx.cancelRuleEdit = function cancelRuleEdit() {
    ctx.rulesEditingId.value = null
  }

  ctx.saveRule = async function saveRule(payload) {
    const mailboxId = payload.mailbox_id || ctx.rulesMailboxId.value
    if (!mailboxId) throw new Error('mailbox required')

    const body = { email_rule: { ...payload, mailbox_id: mailboxId } }
    let data
    if (payload.id) {
      data = await ctx.apiPatch(buildEmailRulePath(emailApiBase(), payload.id), body)
      ctx.rules.value = ctx.rules.value.map(r => (r.id === data.id ? data : r))
    } else {
      data = await ctx.apiPost(buildEmailRulesPath(emailApiBase()), body)
      ctx.rules.value = [...ctx.rules.value, data]
    }
    ctx.rulesEditingId.value = null
    return data
  }

  ctx.toggleRule = async function toggleRule(ruleId) {
    const data = await ctx.apiPatch(buildEmailRuleTogglePath(emailApiBase(), ruleId), {})
    ctx.rules.value = ctx.rules.value.map(r => (r.id === data.id ? data : r))
    return data
  }

  ctx.deleteRule = async function deleteRule(ruleId) {
    await ctx.apiDelete(buildEmailRulePath(emailApiBase(), ruleId))
    ctx.rules.value = ctx.rules.value.filter(r => r.id !== Number(ruleId))
    if (ctx.rulesEditingId.value === Number(ruleId)) ctx.rulesEditingId.value = null
  }

  ctx.reorderRules = async function reorderRules(ids) {
    const mailboxId = ctx.rulesMailboxId.value
    if (!mailboxId) return
    await ctx.apiPost(buildEmailRulesReorderPath(emailApiBase()), {
      ids,
      mailbox_id: mailboxId,
    })
    const byId = Object.fromEntries(ctx.rules.value.map(r => [r.id, r]))
    ctx.rules.value = ids.map((id, index) => ({ ...byId[id], position: index })).filter(Boolean)
  }

  ctx.setDraftId = function setDraftId(id) {
    ctx.draftId.value = id ? Number(id) : null
  }

  ctx.markComposeDirty = function markComposeDirty() {
    ctx.composeDirty.value = true
  }

  ctx.markComposeClean = function markComposeClean() {
    ctx.composeDirty.value = false
  }

  // Task create / link modals (Preact) — signals live on store root
  ctx.openTaskCreateModal = function openTaskCreateModal(emailId) {
    ctx.taskLinkPickerOpen.value = false
    ctx.taskLinkEmailId.value = null
    ctx.taskCreateEmailId.value = Number(emailId)
    ctx.taskCreateOpen.value = true
  }
  ctx.closeTaskCreateModal = function closeTaskCreateModal() {
    ctx.taskCreateOpen.value = false
    ctx.taskCreateEmailId.value = null
  }
  ctx.openTaskLinkPicker = function openTaskLinkPicker(emailId) {
    ctx.taskCreateOpen.value = false
    ctx.taskCreateEmailId.value = null
    ctx.taskLinkEmailId.value = Number(emailId)
    ctx.taskLinkPickerOpen.value = true
  }
  ctx.closeTaskLinkPicker = function closeTaskLinkPicker() {
    ctx.taskLinkPickerOpen.value = false
    ctx.taskLinkEmailId.value = null
  }
}
